import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getTierBySlug, isTierSlug, PRICING_TIERS, TIER_ORDER, TierSlug } from '../data/pricingTiers';
import ResendVerification from '../components/ResendVerification';
import { authAPI } from '../services/api';

type Step = 'plan' | 'account' | 'success';
type Tier = TierSlug;

// Serves both /register and /registerwithpayment.
const RegisterWithPayment: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Only a real plan counts: a missing or unknown ?tier= shows the plan picker
  const tierParam = searchParams.get('tier');
  const urlTier: Tier | null = isTierSlug(tierParam) ? tierParam : null;

  // If a tier was passed via URL, skip straight to account creation
  const [step, setStep] = useState<Step>(urlTier ? 'account' : 'plan');
  // Without one, the selection falls back to the lowest plan (Beginner)
  const [selectedTier, setSelectedTier] = useState<Tier>(urlTier ?? TIER_ORDER[0]);
  
  const [formData, setFormData] = useState({
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(true);
  const [loading, setLoading] = useState(false);

  const handlePlanSelect = (tier: Tier) => {
    setSelectedTier(tier);
    setError('');
    setStep('account');
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.email !== formData.confirmEmail) {
      setError('Email addresses do not match');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      // Create account — pass selected tier so backend embeds it in verification email URL
      const res = await authAPI.register(
        {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
        },
        selectedTier
      );

      // The account is created even when the verification email fails to send;
      // only an explicit false means delivery failed (older responses omit it).
      setEmailSent(res.data?.email_sent !== false);

      // Show success — user must verify email first, then they'll be redirected to Stripe for paid tiers
      setStep('success');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  const tierLabels: Record<Tier, string> = PRICING_TIERS.reduce(
    (acc, t) => ({ ...acc, [t.slug]: `${t.name} — ${t.price}/mo` }),
    {} as Record<Tier, string>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      {/* SEO — React 19 hoists to <head> */}
      <title>Sign Up — NWC-Analytics</title>
      <meta name="description" content="Create your NWC-Analytics account and choose a subscription plan to unlock real-time stock analysis tools." />
      <link rel="canonical" href="https://nwc-analytics.com/registerwithpayment" />
      <div className="max-w-6xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src="/images/logo.png" alt="NWC-Analytics" className="h-16 w-16 mx-auto" />
            <span className="text-xl font-bold text-primary-400 dark:text-primary-400" style={{ fontFamily: "'Viner Hand ITC', 'Caveat', cursive", fontSize: '1rem', fontStyle: 'italic' }}>NWC-Analytics</span>
          </Link>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className={`flex items-center ${step === 'plan' ? 'text-primary-600 dark:text-primary-400' : step === 'account' || step === 'success' ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 text-sm font-bold ${
                step === 'plan' 
                  ? 'border-primary-600 bg-primary-600 text-white' 
                  : step === 'account' || step === 'success'
                  ? 'border-green-600 bg-green-600 text-white'
                  : 'border-gray-300 text-gray-400'
              }`}>
                {step === 'account' || step === 'success' ? '✓' : '1'}
              </div>
              <span className="ml-2 font-medium">Choose Plan</span>
            </div>
            
            <div className={`w-16 h-1 mx-4 ${step !== 'plan' ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
            
            <div className={`flex items-center ${step === 'account' ? 'text-primary-600 dark:text-primary-400' : step === 'success' ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 text-sm font-bold ${
                step === 'account' 
                  ? 'border-primary-600 bg-primary-600 text-white' 
                  : step === 'success'
                  ? 'border-green-600 bg-green-600 text-white'
                  : 'border-gray-300 text-gray-400'
              }`}>
                {step === 'success' ? '✓' : '2'}
              </div>
              <span className="ml-2 font-medium">Create Account</span>
            </div>
            
            <div className={`w-16 h-1 mx-4 ${step === 'success' ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
            
            <div className={`flex items-center ${step === 'success' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 text-sm font-bold ${
                step === 'success' ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 text-gray-400'
              }`}>
                3
              </div>
              <span className="ml-2 font-medium">Get Started</span>
            </div>
          </div>
        </div>

        {/* Step 1: Plan Selection */}
        {step === 'plan' && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                Choose Your Plan
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Pick the plan that fits your investing style — you can always upgrade later
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {PRICING_TIERS.map((tier) => (
                <div
                  key={tier.slug}
                  className={`bg-white dark:bg-gray-700 rounded-xl shadow-lg border p-6 transition-all ${
                    tier.popular
                      ? 'ring-2 ring-primary-500 transform scale-105'
                      : 'dark:border-gray-500 hover:shadow-xl'
                  }`}
                >
                  {tier.popular && (
                    <div className="text-center mb-2">
                      <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        POPULAR
                      </span>
                    </div>
                  )}

                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {tier.name}
                  </h3>

                  <div className="mb-4">
                    <span className="text-3xl font-extrabold text-gray-900 dark:text-white">
                      {tier.price}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {tier.period}
                    </span>
                    {tier.hasTrial && tier.trialBadge && (
                      <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mt-1">{tier.trialBadge}</p>
                    )}
                  </div>

                  <ul className="space-y-2 mb-6">
                    {tier.highlightedBullet && (
                      <li className="flex items-start text-sm">
                        <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="font-bold text-gray-900 dark:text-white">{tier.highlightedBullet}</span>
                      </li>
                    )}
                    {tier.bullets.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-900 dark:text-white">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanSelect(tier.slug)}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                      tier.popular
                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                        : 'bg-gray-900 hover:bg-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 text-white'
                    }`}
                  >
                    {tier.ctaLabel}
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary-600 dark:text-primary-400 hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Create Account */}
        {step === 'account' && (
          <div className="max-w-md mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                Create Your Account
              </h2>
              <div className="mt-3 inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200">
                Selected Plan: {tierLabels[selectedTier]}
              </div>
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary-600 dark:text-primary-400 hover:underline">
                  Sign in
                </Link>
              </p>
            </div>

            <div className="bg-white dark:bg-gray-700 py-8 px-6 shadow-xl rounded-lg border dark:border-gray-500">
              <form onSubmit={handleAccountSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Full name
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email address
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Confirm email address
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.confirmEmail}
                    onChange={(e) => setFormData({ ...formData, confirmEmail: e.target.value })}
                    onPaste={(e) => e.preventDefault()}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Must be at least 8 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading 
                    ? 'Creating account...' 
                    : getTierBySlug(selectedTier).hasTrial
                    ? 'Create Account & Start Free Trial'
                    : 'Create Account & Continue to Payment'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => { setStep('plan'); setError(''); }}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  ← Change plan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 'success' && (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-gray-700 py-8 px-6 shadow-xl rounded-lg border dark:border-gray-500 text-center">
              {emailSent ? (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/50 mb-4">
                    <svg className="h-10 w-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Check Your Email!
                  </h3>

                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    We've sent a verification link to <strong className="text-gray-900 dark:text-white">{formData.email}</strong>
                  </p>

                  {selectedTier && (
                    <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg p-4 mb-4">
                      <p className="text-sm text-primary-800 dark:text-primary-200">
                        <strong>Next step:</strong> After verifying your email, you'll be automatically redirected to complete your <strong>{getTierBySlug(selectedTier).name}</strong> subscription payment.
                      </p>
                    </div>
                  )}

                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>📧 Can't find the email?</strong> Check your <strong>spam or junk folder</strong> — verification emails sometimes end up there.
                    </p>
                  </div>

                  <Link
                    to="/login"
                    className="inline-block px-6 py-3 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
                  >
                    Go to Login
                  </Link>
                </>
              ) : (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900/50 mb-4">
                    <svg className="h-10 w-10 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
                    </svg>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Account created — but the email didn't send
                  </h3>

                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Your account for <strong className="text-gray-900 dark:text-white">{formData.email}</strong> exists,
                    but we couldn't deliver the verification link. You won't be able to log in until it's verified.
                  </p>

                  <ResendVerification
                    email={formData.email}
                    selectedTier={selectedTier || undefined}
                    className="mb-4 text-left"
                  />

                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Still stuck? Email{' '}
                    <a href="mailto:support@nwc-analytics.com" className="text-primary-600 dark:text-primary-400 hover:underline">
                      support@nwc-analytics.com
                    </a>
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterWithPayment;